import "../../assets/ContentEditor.css";
import React, { memo, useMemo, useState } from "react";
import { Card } from "primereact/card";
import { ExplainedButton as Button } from '../explained-button';
import { Badge } from "primereact/badge";
import { ManagedFragmentDto, FragmentActionDto, getActionText } from "../../general/dtos";
import { DeleteEntityConfirmDialog } from "../dialogs/delete-entity-confirm";

interface FragmentNodeProps {
  fragment: ManagedFragmentDto;
  isSelected: boolean;
  onSelect: (key: string) => void;
  // Hoisted Modal Triggers
  onAddAction: (fragment: ManagedFragmentDto) => void;
  onEditAction: (fragment: ManagedFragmentDto, action: FragmentActionDto) => void;
  // Action logic passed from parent
  removeAction: (fragmentKey: string, actionKey: string) => void;
  updateActionsOrder: (fragmentKey: string, next: FragmentActionDto[]) => void;
  // Needed for getActionText to resolve "Go to" labels
  allFragments: ManagedFragmentDto[];
}

/**
 * Memoized FragmentNode: Only re-renders if:
 * 1. The specific fragment data changes.
 * 2. The selection state changes.
 * 3. The list of all fragments changes (relevant for action text resolution).
 */
export const FragmentNode = memo(({ 
  fragment, 
  isSelected, 
  onSelect,
  onAddAction,
  onEditAction,
  removeAction,
  updateActionsOrder,
  allFragments
}: FragmentNodeProps) => {
  // Memoize the sorted actions to avoid sorting on every render
  const sortedActions = useMemo(() => {
    return [...fragment.actions].sort((a, b) => a.order - b.order);
  }, [fragment.actions]);

  const [actionToDelete, setActionToDelete] = useState<FragmentActionDto | null>(null);

  const handleAddAction = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAddAction(fragment);
  };

  const handleRemoveAction = (actionKey: string) => {
    removeAction(fragment.key, actionKey);
  };

  const moveAction = (index: number, direction: 'up' | 'down') => {
    const neighborIndex = direction === 'up' ? index - 1 : index + 1;
    if (neighborIndex < 0 || neighborIndex >= sortedActions.length) return;

    const current = { ...sortedActions[index] };
    const neighbor = { ...sortedActions[neighborIndex] };

    const tempOrder = current.order;
    current.order = neighbor.order;
    neighbor.order = tempOrder;

    // Create a new array with swapped items
    const nextActions = [...sortedActions];
    nextActions[index] = current;
    nextActions[neighborIndex] = neighbor;

    updateActionsOrder(fragment.key, nextActions);
  };

  const header = (
    <div className="flex align-items-center justify-content-between py-1">
      <div className="font-bold">{fragment.title}
        {Boolean(fragment.hidden) && (<i 
            className="pi pi-eye-slash text-color-secondary" 
            style={{ fontSize: '0.9rem', paddingLeft: '1rem' }} 
            title="This fragment is hidden"
            />
        )}</div>
      <Button 
        icon="pi pi-plus" 
        className="p-button-text p-button-sm p-0" 
        onClick={handleAddAction} 
        tooltip="Add action" 
      />
    </div>
  );

  return (
    <div
      onClick={() => onSelect(fragment.key)}
      role="option"
      tabIndex={0}
      aria-selected={isSelected}
      className={isSelected ? "p-card p-card-highlight" : "p-card"}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter") {
            e.preventDefault();
            onSelect(fragment.key);
        }
      }}
    >
      <Card header={header} className="mb-1 compact-card" style={{ cursor: "pointer" }}>
        <div className="flex flex-column gap-1">
          {sortedActions.map((action, index) => (
            <div
              key={action.key}
              className="flex align-items-center justify-content-between border-1 surface-border p-1 border-round"
            >
              <div className="flex align-items-center gap-1">
                <Badge value={action.type} severity="info" />
                <span>{getActionText(action, allFragments)}</span>
              </div>

              <div className="flex gap-0">
                <Button 
                  icon="pi pi-angle-up" 
                  className="p-button-text p-button-sm p-1" 
                  disabled={index === 0} 
                  disabledReason="This is already the first action in this fragment."
                  onClick={(e) => { e.stopPropagation(); moveAction(index, 'up'); }} 
                  tooltip="Move up" 
                  style={{ width: '1.75rem' }}
                />
                <Button 
                  icon="pi pi-angle-down" 
                  className="p-button-text p-button-sm p-1" 
                  disabled={index === sortedActions.length - 1} 
                  disabledReason="This is already the last action in this fragment."
                  onClick={(e) => { e.stopPropagation(); moveAction(index, 'down'); }} 
                  tooltip="Move down" 
                  style={{ width: '1.75rem' }}
                />
                <Button 
                  icon="pi pi-pencil" 
                  className="p-button-text p-button-sm p-1" 
                  onClick={(e) => { e.stopPropagation(); onEditAction(fragment, action); }} 
                  tooltip="Edit action" 
                  style={{ width: '1.75rem' }}
                />
                <Button 
                  icon="pi pi-trash" 
                  className="p-button-text p-button-sm p-1" 
                  onClick={(e) => { e.stopPropagation(); setActionToDelete(action); }} 
                  tooltip="Remove action" 
                  style={{ width: '1.75rem' }}
                />
              </div>
            </div>
          ))}
        </div>
      </Card>

      <DeleteEntityConfirmDialog 
        visible={actionToDelete !== null}
        onCancel={() => setActionToDelete(null)}
        onConfirm={async () => {
            if (actionToDelete) {
                handleRemoveAction(actionToDelete.key);
            }
            setActionToDelete(null);
        }}
        entityType='Action'
        entityName={actionToDelete?.label ?? ''}
      />
    </div>
  );
}, (prev, next) => {
  // Deep comparison to prevent unnecessary re-renders
  return (
    prev.isSelected === next.isSelected &&
    prev.fragment === next.fragment &&
    prev.allFragments === next.allFragments &&
    prev.onAddAction === next.onAddAction &&
    prev.onEditAction === next.onEditAction
  );
});

// Explicit display name for debugging in React DevTools
FragmentNode.displayName = "FragmentNode";
